import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Target, Users, Award, Brain, Sparkles } from 'lucide-react';

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen py-12 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <span className="text-xs uppercase tracking-widest text-secondary font-medium">
            À propos
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2 mb-6">
            Notre mission
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Le Journal de pratiques et recherches en psychothérapie a pour vocation de promouvoir 
            une psychothérapie moderne, rigoureuse et innovante.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-16">
          <Card className="bg-primary text-primary-foreground">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-semibold mb-4">Notre vision</h2>
                  <p className="leading-relaxed opacity-90">
                    La revue s'inscrit dans la tradition des thérapies comportementales et cognitives (TCC) 
                    tout en explorant de nouvelles approches telles que le transdiagnostique, l'utilisation 
                    de l'intelligence artificielle en santé mentale, et l'intégration des technologies 
                    numériques dans la pratique clinique.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Objectives */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold mb-6">Nos objectifs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <BookOpen className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-semibold mb-2">Diffuser</h3>
                <p className="text-sm text-muted-foreground">
                  Des travaux cliniques et scientifiques de haute qualité accessibles aux praticiens et chercheurs.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Sparkles className="w-8 h-8 text-secondary mb-4" />
                <h3 className="font-semibold mb-2">Encourager</h3>
                <p className="text-sm text-muted-foreground">
                  Les études empiriques, les innovations thérapeutiques et les recherches transdiagnostiques.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Users className="w-8 h-8 text-accent-foreground mb-4" />
                <h3 className="font-semibold mb-2">Favoriser</h3>
                <p className="text-sm text-muted-foreground">
                  Le dialogue entre pratique clinique et avancées scientifiques.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Types of contributions */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold mb-6">Types de contributions acceptées</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Articles de recherche empirique',
              'Études de cas rigoureuses',
              'Recherches théoriques et critiques',
              'Revues de littérature',
              'Présentations d\'innovations technologiques',
              'Notes de pratique clinique',
              'Protocoles de recherche'
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Editorial values */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold mb-6">Valeurs éditoriales</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: 'Rigueur scientifique' },
              { icon: BookOpen, title: 'Clarté de l\'écriture' },
              { icon: Target, title: 'Pertinence clinique' },
              { icon: Sparkles, title: 'Innovation' },
              { icon: Users, title: 'Respect éthique' },
              { icon: Brain, title: 'Anonymisation' }
            ].map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">{value.title}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Review process */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold mb-6">Processus d'évaluation</h2>
          <Card>
            <CardContent className="p-8">
              <p className="text-muted-foreground leading-relaxed mb-6">
                Chaque manuscrit reçu fait l'objet d'une première lecture attentive, centrée sur la 
                pertinence et la clarté du propos. Si le projet s'inscrit dans notre ligne éditoriale, 
                il est alors accompagné tout au long du processus d'évaluation avec bienveillance et réactivité.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <span className="text-sm">Soumission</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <span className="text-sm">Lecture initiale</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <span className="text-sm">Évaluation</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    4
                  </div>
                  <span className="text-sm">Publication</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t">
          <h2 className="font-serif text-2xl font-semibold mb-4">
            Notre ambition est simple
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Encourager le partage d'expériences et la réflexion critique, au service d'une 
            psychothérapie toujours plus vivante, éclairée et humaine.
          </p>
        </section>
      </div>
    </div>
  );
}
